import React, { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaHandshake,
  FaShieldAlt,
  FaNetworkWired,
  FaArrowRight,
} from "react-icons/fa";
import ourvision from "../../../assest/images/Ourvision.png";

/* ------------------------------------------------------------------ */
/*  Section-scoped styles (ov- prefix avoids hero/about collisions)   */
/* ------------------------------------------------------------------ */
const VISION_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .ov-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .ov-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes ov-morph {
    0%, 100% { border-radius: 62% 38% 55% 45% / 55% 45% 55% 45%; }
    25%      { border-radius: 45% 55% 40% 60% / 60% 40% 55% 45%; }
    50%      { border-radius: 55% 45% 62% 38% / 40% 62% 38% 60%; }
    75%      { border-radius: 38% 62% 45% 55% / 55% 45% 60% 40%; }
  }
  @keyframes ov-float-a {
    0%, 100% { transform: translate(0, 0) rotate(-3deg); }
    50%      { transform: translate(4px, -8px) rotate(2deg); }
  }
  @keyframes ov-float-b {
    0%, 100% { transform: translate(0, 0) rotate(2deg); }
    50%      { transform: translate(-6px, 5px) rotate(-3deg); }
  }
  @keyframes ov-dash { to { stroke-dashoffset: -44; } }
  @keyframes ov-ring-spin { to { transform: rotate(360deg); } }
  @keyframes ov-tape-sway {
    0%, 100% { transform: rotate(-5deg); }
    50%      { transform: rotate(-1deg); }
  }

  .ov-morph-frame { animation: ov-morph 11s ease-in-out infinite; }
  .ov-float-a { animation: ov-float-a 8s ease-in-out infinite; }
  .ov-float-b { animation: ov-float-b 9s ease-in-out infinite; }
  .ov-dash-anim { animation: ov-dash 2.8s linear infinite; }
  .ov-ring-spin { animation: ov-ring-spin 30s linear infinite; }
  .ov-tape-sway { animation: ov-tape-sway 5.5s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .ov-morph-frame, .ov-float-a, .ov-float-b,
    .ov-dash-anim, .ov-ring-spin, .ov-tape-sway { animation: none !important; }
  }
`;


/* Pillar data — each has a unique color stop & asymmetric radius to feel
   hand-crafted rather than machine-stamped. */
const pillars = [
  {
    icon: <FaHandshake />,
    title: "Long-Term Growth & Scale",
    desc: "We strongly believe in developing long term business relationship with our direct clients and end users by focused working and productive follow up of all feedbacks. Our focus is to claim thousand million impressions as ad network and the first choice for content owner to stream their content.",
    color: "#D97706",
    bg: "#FEF3E5",
    border: "#FDE8CD",
    radius: "2rem 0.8rem 2rem 0.8rem",
    tilt: -1.5,
  },
  {
    icon: <FaShieldAlt />,
    title: "Brand Safety & Safe Ecosystem",
    desc: "To provide a safe environment to our Advertisers/Publishers and provide brand safety to platforms both on buy/sell side when scaling up the business and meeting objectives for our publishers/advertisers.",
    color: "#EA580C",
    bg: "#FFF4E5",
    border: "#FDE4C8",
    radius: "0.8rem 2rem 0.8rem 2rem",
    tilt: 1.5,
  },
  {
    icon: <FaNetworkWired />,
    title: "Ecosystem Partnerships & RTB",
    desc: "We partner with content providers, demand players and tech platforms. RTB DEMAND harnesses programmatic buying and selling across the ecosystem.",
    color: "#D97706",
    bg: "#FFF4E5",
    border: "#FDE8CD",
    radius: "2rem 0.8rem 2rem 0.8rem",
    tilt: -1,
  },
];

function Ourvision() {
  const imgWrap = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.12 });

  const handleMouseMove = (e) => {
    if (prefersReducedMotion) return;
    const el = imgWrap.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 24;
    const y = (e.clientY - rect.top - rect.height / 2) / 24;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };

  const resetTransform = () => {
    const el = imgWrap.current;
    if (el) el.style.transform = "translate(0px, 0px)";
  };

  return (
    <section
      id="ourVisionID"
      ref={ref}
      className="ov-body relative py-20 md:py-32 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(100% 80% at 0% 15%, #FEF3E5 0%, transparent 55%), radial-gradient(90% 80% at 100% 85%, #FFF4E5 0%, transparent 55%), radial-gradient(70% 60% at 50% 50%, #FFF4E5 0%, transparent 50%), #FFFBF5",
      }}
    >
      <style>{VISION_STYLES}</style>

      {/* ---- ambient blobs ---- */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="ov-float-a absolute -top-20 -left-20 w-[28rem] h-[28rem] opacity-[0.30] blur-[100px]"
          style={{ background: "radial-gradient(circle, #FDDBB8, transparent 70%)" }}
        />
        <div
          className="ov-float-b absolute -bottom-16 -right-16 w-[24rem] h-[24rem] opacity-[0.25] blur-[90px]"
          style={{ background: "radial-gradient(circle, #FDDCB5, transparent 70%)" }}
        />
      </div>


      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        {/* ================ SECTION HEADER ================ */}
       <motion.div
                 initial={{ opacity: 0, y: 24 }}
                 animate={inView ? { opacity: 1, y: 0 } : {}}
                 transition={{ duration: 0.6 }}
                 className="text-center mb-16 md:mb-20 max-w-2xl mx-auto"
               >
                 <div className="flex items-center w-full gap-4 mb-5">
                   <span
                     className="hidden sm:inline-block h-[2px] flex-1"
                     style={{
                       background: "linear-gradient(to left, #EA580C, transparent)",
                     }}
                   />
                   <span className="text-xs sm:text-sm font-bold tracking-wide text-[#92400E] whitespace-nowrap">
                     Company Journey &amp; Vision
                   </span>
                   <span
                     className="hidden sm:inline-block h-[2px] flex-1"
                     style={{
                       background: "linear-gradient(to right, #EA580C, transparent)",
                     }}
                   />
                 </div>

          <h2
            className="ov-display text-3xl sm:text-4xl lg:text-[3.2rem] text-[#1C1410] leading-[1.12]"
            style={{ fontWeight: 600 }}
          >
            What we're building{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]"
                style={{ fontStyle: "italic", fontWeight: 500 }}
              >
                toward
              </span>
              <svg
                className="absolute left-0 -bottom-1.5 w-full"
                height="9"
                viewBox="0 0 200 9"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M2,6 C40,1 70,8 100,4 C130,0 160,7 198,3"
                  fill="none"
                  stroke="url(#ovUnderline)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="ovUnderline" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#D97706" />
                    <stop offset="100%" stopColor="#EA580C" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>

          <p className="mt-4 text-[#4D3E30] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Three pillars guide every product decision we make —
            long-term partnerships, uncompromising safety, and an open
            ecosystem built for scale.
          </p>
        </motion.div>

        {/* ================ CONTENT GRID ================ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* ---------- LEFT: morphing image cluster ---------- */}
          <motion.div
            initial={{ opacity: 0, x: -40, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative flex justify-center order-2 lg:order-1 px-6 sm:px-2"
          >
            <div
              className="relative w-full max-w-[15rem] xs:max-w-[17rem] sm:max-w-sm"
              onMouseMove={handleMouseMove}
              onMouseLeave={resetTransform}
            >
              {/* Spinning dashed ring behind the image */}
              <svg
                aria-hidden="true"
                className="absolute inset-0 w-full h-full -z-10 opacity-[0.18] ov-ring-spin"
                viewBox="0 0 300 300"
              >
                <circle
                  cx="150"
                  cy="150"
                  r="140"
                  fill="none"
                  stroke="url(#ovRingGrad)"
                  strokeWidth="1.5"
                  strokeDasharray="8 12"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="ovRingGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D97706" />
                    <stop offset="50%" stopColor="#EA580C" />
                    <stop offset="100%" stopColor="#D97706" />
                  </linearGradient>
                </defs>
              </svg>

              {/* washi-tape corner */}
              <span
                aria-hidden="true"
                className="hidden sm:block absolute -top-5 left-6 w-14 h-6 ov-tape-sway z-20"
                style={{
                  background: "repeating-linear-gradient(135deg, #FDE8C8 0 7px, #FDDCB0 7px 14px)",
                  borderRadius: "2px",
                  boxShadow: "0 3px 8px rgba(22,34,27,0.12)",
                  transform: "rotate(-5deg)",
                }}
              />

              {/* Main image — morphing organic blob frame */}
              <div
                ref={imgWrap}
                className="ov-morph-frame relative w-full aspect-square overflow-hidden shadow-[0_30px_70px_-25px_rgba(22,34,27,0.25)] border-[3px] border-white transition-transform duration-200 ease-out"
                style={{ background: "linear-gradient(160deg, #fff, #FFF8F2)" }}
              >
                <img
                  src={ourvision}
                  alt="MyAd360 vision illustration"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating stat chip — top right */}
              <motion.div
                initial={{ opacity: 0, y: -14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="absolute -top-3 -right-2 sm:-right-6 md:-right-8 z-20 bg-white px-2.5 sm:px-3.5 py-2 sm:py-2.5 shadow-xl border border-[#FDE8CD] flex items-center gap-2 sm:gap-2.5 max-w-[9.5rem] sm:max-w-none"
                style={{ borderRadius: "1.4rem 0.5rem 1.4rem 0.5rem", transform: "rotate(3deg)" }}
              >
                <span className="w-2 h-2 rounded-full bg-[#D97706] animate-pulse shrink-0" />
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-[#8A7560] font-semibold whitespace-nowrap">Programmatic</p>
                  <p className="text-[11px] sm:text-xs font-extrabold text-[#1C1410] whitespace-nowrap">RTB Ecosystem</p>
                </div>
              </motion.div>

              {/* Floating stat chip — bottom left */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.85 }}
                className="absolute -bottom-4 -left-2 sm:-left-4 md:-left-6 z-20 bg-[#1C1410] text-white px-2.5 sm:px-3.5 py-2 sm:py-2.5 shadow-2xl flex items-center gap-2 sm:gap-2.5 max-w-[9.5rem] sm:max-w-none"
                style={{ borderRadius: "0.5rem 1.4rem 0.5rem 1.4rem", transform: "rotate(-3deg)" }}
              >
                <FaShieldAlt className="text-[#FBD38D] text-sm flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-slate-400 font-semibold whitespace-nowrap">Brand Safety</p>
                  <p className="text-[11px] sm:text-xs font-bold text-[#FBD38D] whitespace-nowrap">Buy &amp; Sell Guard</p>
                </div>
              </motion.div>

              {/* decorative sparkle dots */}
              <span
                aria-hidden="true"
                className="hidden sm:block absolute top-1/4 -right-3 w-2 h-2 rounded-full bg-[#D97706] ov-float-a"
              />
              <span
                aria-hidden="true"
                className="hidden sm:block absolute bottom-1/4 -left-2 w-1.5 h-1.5 rounded-full bg-[#EA580C] ov-float-b"
                style={{ animationDelay: "-2s" }}
              />
            </div>
          </motion.div>

          {/* ---------- RIGHT: pillar cards with stitched spine ---------- */}
          <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
            {/* Stitched connector spine — a hand-drawn dashed line
                running along the left edge behind the pillar cards */}
              <div className="space-y-5">
                {pillars.map((p, i) => (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 30, x: 20 }}
                    animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
                    transition={{
                      duration: 0.55,
                      delay: 0.25 + i * 0.15,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={prefersReducedMotion ? {} : { y: -5, rotate: 0 }}
                    className="group relative bg-white/90 backdrop-blur-sm border border-[#F0DCC8] shadow-sm hover:shadow-[0_20px_50px_-20px_rgba(22,34,27,0.22)] transition-all duration-300 p-6 sm:p-7 overflow-hidden"
                    style={{
                      borderRadius: p.radius,
                      transform: `rotate(${p.tilt}deg)`,
                    }}
                  >
                    {/* top accent stripe */}
                    <div
                      className="absolute top-0 left-8 right-8 h-[3px] rounded-b-full"
                      style={{ background: p.color }}
                    />

                    <div className="flex items-start gap-4 sm:gap-5">
                      {/* medal icon — organic asymmetric shape */}
                      <div
                        className="w-12 h-12 shrink-0 flex items-center justify-center text-lg shadow-md border-2 border-white z-10 transition-all duration-500 group-hover:scale-110 mt-0.5"
                        style={{
                          background: `linear-gradient(145deg, ${p.bg}, ${p.border})`,
                          color: p.color,
                          borderRadius: "58% 42% 55% 45% / 45% 55% 42% 58%",
                        }}
                      >
                        {p.icon}
                      </div>

                      <div className="flex-1">
                        <h3
                          className="ov-display text-base sm:text-lg text-[#1C1410] mb-2 transition-colors duration-300 font-semibold"
                        >
                          <span className={`group-hover:text-[${p.color}] transition-colors`}>
                            {p.title}
                          </span>
                        </h3>
                        <p className="text-[#4D3E30] text-xs sm:text-sm leading-relaxed">
                          {p.desc}
                        </p>

                        {/* subtle reveal arrow on hover */}
                        <div
                          className="mt-4 pt-3 border-t border-[#F5DFC5] flex items-center gap-2 text-xs font-bold opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all duration-300"
                          style={{ color: p.color }}
                        >
                          <span>Learn more</span>
                          <FaArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    {/* bottom-right curl flourish on hover */}
                    <svg
                      aria-hidden="true"
                      className="absolute -bottom-1 -right-1 w-12 h-12 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M44,4 C44,30 30,44 4,44"
                        fill="none"
                        stroke={p.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="80"
                        strokeDashoffset="80"
                        className="group-hover:[stroke-dashoffset:0] transition-all duration-700"
                      />
                    </svg>
                  </motion.div>
                ))}
              </div>

            {/* CTA after pillars */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="pt-2"
            >
              <a
                href="#contactID"
                className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white font-bold px-7 py-3.5 shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 text-sm sm:text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D97706]"
                style={{ borderRadius: "1.8rem 0.6rem 1.8rem 0.6rem" }}
              >
                <span className="relative z-10 flex items-center gap-2.5">
                  Partner With Us
                  <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
                />
              </a>
            </motion.div>
          </div>
        </div>
      </div>

    </section>
  );
}

export default Ourvision;